/**
 * The widget's copy, in the two languages it ships with.
 *
 * A flat record rather than a library: a translation framework would cost more
 * bytes than the strings themselves on a bundle that loads on every page of a
 * customer's site. Every word a visitor sees comes from here or from the
 * site's own settings — never from a server error message, which is written
 * for developers and in whatever language the server happens to speak.
 */

export type ErrorCode =
  | "imageType"
  | "imageTooBig"
  | "uploadsOff"
  | "uploadFailed"
  | "storageFull"
  | "chatFileLimit"
  | "network"
  | "startFailed"
  | "sendFailed"
  | "tooFast"
  | "busy"
  | "siteBusy"
  | "blocked"
  | "linksBlocked"
  | "repeated"
  | "contactRequired";

/**
 * Refusals where the server kept nothing, so the message is withdrawn (and
 * handed back to the input), not retried: the site's spam rules, and the
 * site's budget for new anonymous chats (siteBusy) — retrying that one in a
 * loop is exactly the traffic it exists to stop. contactRequired is the site
 * asking who the visitor is first; the text waits while they answer.
 */
export const MODERATION_CODES: readonly ErrorCode[] = ["blocked", "linksBlocked", "repeated", "siteBusy", "contactRequired"];

export function moderationCode(code: unknown): ErrorCode | null {
  return typeof code === "string" && (MODERATION_CODES as readonly string[]).includes(code) ? (code as ErrorCode) : null;
}

type Strings = {
  open: string;
  close: string;
  online: string;
  offline: string;
  send: string;
  typing: string;
  you: string;
  team: string;
  sending: string;
  offlineTitle: string;
  offlineHint: string;
  email: string;
  name: string;
  message: string;
  start: string;
  rateTitle: string;
  /** Shown in the thread once the operator ends the conversation. */
  ended: string;
  /** Composer placeholder while the conversation is ended. */
  endedPlaceholder: string;
  /** Under a finished conversation: start over with an empty thread. */
  newChat: string;
  rateThanks: string;
  connecting: string;
  reconnecting: string;
  attach: string;
  offlineSend: string;
  offlineSent: string;
  offlineSentHint: string;
  optional: string;
  faqTitle: string;
  faqMore: string;
  newMessage: string;
  /** The word on a pill or tab launcher when the site has not written its own. */
  chat: string;
  /** The small attribution link in the panel footer. */
  poweredBy: string;
  /** Beside the time of a reply the operator corrected after sending it. */
  edited: string;
  /** Above the form a site puts in front of the first message. */
  contactTitle: string;
  contactHint: string;
  /** The question asked inside the thread, after the first message. */
  contactAsk: string;
  contactSave: string;
  contactSkip: string;
  contactThanks: string;
} & Record<ErrorCode, string>;

const tr: Strings = {
  open: "Canlı desteği aç",
  close: "Kapat",
  online: "Çevrimiçi",
  offline: "Çevrimdışı",
  send: "Gönder",
  typing: "yazıyor…",
  you: "Siz",
  team: "Destek",
  sending: "gönderiliyor…",
  offlineTitle: "Şu anda çevrimdışıyız",
  offlineHint: "E-posta adresinizi bırakın, dönüş yapalım.",
  email: "E-posta",
  name: "Adınız",
  message: "Mesajınız",
  start: "Sohbeti başlat",
  rateTitle: "Görüşme nasıldı?",
  ended: "Bu sohbet sona erdi.",
  endedPlaceholder: "Başka bir sorunuz mu var?",
  newChat: "Yeni sohbet başlat",
  rateThanks: "Teşekkürler!",
  connecting: "Bağlanıyor…",
  reconnecting: "Yeniden bağlanılıyor…",
  attach: "Resim ekle",
  offlineSend: "Mesajı gönder",
  offlineSent: "Mesajınız bize ulaştı.",
  offlineSentHint: "Mesai saatlerinde e-posta adresinize dönüş yapacağız.",
  optional: "isteğe bağlı",
  faqTitle: "Sık sorulanlar",
  faqMore: "Aradığınızı bulamadınız mı? Bize yazın.",
  newMessage: "Yeni mesaj",
  chat: "Sohbet",
  poweredBy: "AgentlyDesk ile",
  edited: "düzenlendi",
  contactTitle: "Başlamadan önce",
  contactHint: "Size adınızla hitap edebilmemiz ve gerekirse e-postayla dönebilmemiz için.",
  contactAsk: "Bağlantı koparsa size dönebilmemiz için e-posta adresinizi bırakır mısınız?",
  contactSave: "Kaydet",
  contactSkip: "Şimdi değil",
  contactThanks: "Teşekkürler, adresinizi aldık.",
  contactRequired: "Sohbete başlamadan önce adınızı ve e-posta adresinizi yazın.",
  blocked: "Mesajınızda izin verilmeyen ifadeler var.",
  linksBlocked: "Mesajlarda bağlantı paylaşılamıyor.",
  repeated: "Aynı mesajı birkaç kez gönderdiniz, lütfen yanıtı bekleyin.",
  imageType: "Yalnızca PNG, JPG, GIF veya WEBP resim gönderebilirsiniz.",
  imageTooBig: "Resim 10 MB'tan büyük olamaz.",
  uploadsOff: "Bu sohbette resim gönderilemiyor.",
  uploadFailed: "Resim gönderilemedi, lütfen tekrar deneyin.",
  storageFull: "Bu sitenin dosya alanı dolu, resim gönderilemiyor.",
  chatFileLimit: "Bu sohbette daha fazla resim gönderilemez.",
  network: "Bağlantı koptu, lütfen tekrar deneyin.",
  startFailed: "Sohbet şu anda açılamıyor.",
  sendFailed: "Mesaj gönderilemedi, lütfen tekrar deneyin.",
  tooFast: "Çok hızlı yazıyorsunuz, lütfen biraz bekleyin.",
  busy: "Sohbet şu anda yoğun, birazdan yeniden bağlanılacak…",
  siteBusy: "Şu anda çok yoğunuz. Lütfen biraz sonra tekrar deneyin ya da hesabınıza giriş yapın.",
};

const en: Strings = {
  open: "Open live chat",
  close: "Close",
  online: "Online",
  offline: "Offline",
  send: "Send",
  typing: "is typing…",
  you: "You",
  team: "Support",
  sending: "sending…",
  offlineTitle: "We're offline right now",
  offlineHint: "Leave your e-mail and we'll get back to you.",
  email: "E-mail",
  name: "Your name",
  message: "Your message",
  start: "Start the chat",
  rateTitle: "How was the conversation?",
  ended: "This conversation has ended.",
  endedPlaceholder: "Another question?",
  newChat: "Start a new chat",
  rateThanks: "Thank you!",
  connecting: "Connecting…",
  reconnecting: "Reconnecting…",
  attach: "Attach an image",
  offlineSend: "Send message",
  offlineSent: "We've got your message.",
  offlineSentHint: "We'll reply to your e-mail during our working hours.",
  optional: "optional",
  faqTitle: "Frequently asked",
  faqMore: "Didn't find your answer? Write to us.",
  newMessage: "New message",
  chat: "Chat",
  poweredBy: "Powered by AgentlyDesk",
  edited: "edited",
  contactTitle: "Before we start",
  contactHint: "So we can call you by your name and reach you by e-mail if needed.",
  contactAsk: "Could you leave your e-mail, so we can get back to you if the chat drops?",
  contactSave: "Save",
  contactSkip: "Not now",
  contactThanks: "Thanks, we've got your address.",
  contactRequired: "Please enter your name and e-mail before starting the chat.",
  blocked: "Your message contains words that aren't allowed.",
  linksBlocked: "Links can't be shared in messages.",
  repeated: "You've sent the same message several times. Please wait for a reply.",
  imageType: "You can only send PNG, JPG, GIF or WEBP images.",
  imageTooBig: "Images can't be larger than 10 MB.",
  uploadsOff: "Images can't be sent in this chat.",
  uploadFailed: "The image couldn't be sent. Please try again.",
  storageFull: "This site's file storage is full, so images can't be sent.",
  chatFileLimit: "No more images can be sent in this chat.",
  network: "The connection dropped. Please try again.",
  startFailed: "The chat can't be opened right now.",
  sendFailed: "Your message couldn't be sent. Please try again.",
  tooFast: "You're sending messages too quickly. Please wait a moment.",
  busy: "Chat is busy right now. Reconnecting in a moment…",
  siteBusy: "We're very busy right now. Please try again a little later, or sign in to your account.",
};

export const COPY: Record<"tr" | "en", Strings> = { tr, en };

export type Copy = Strings;

/**
 * An upload's answer, as something a visitor can act on.
 *
 * The body's `code` wins where the server sent one: "storage_full" also
 * arrives as a 413, which on its own would read as "your image is too big"
 * and send the visitor off shrinking a screenshot that was never the problem.
 */
export function uploadErrorCode(status: number, code?: string): ErrorCode {
  if (code === "storage_full") return "storageFull";
  if (code === "conversation_attachment_limit") return "chatFileLimit";
  if (status === 415) return "imageType";
  if (status === 413) return "imageTooBig";
  if (status === 403) return "uploadsOff";
  if (status === 429) return "tooFast";
  if (status === 0) return "network";
  return "uploadFailed";
}
